import { useCallback, useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode, RefObject } from 'react'

import { getCharsCountForService } from '@buffer-mono/char-toolkit'
import {
  Button,
  CoachMark,
  SquarePenIcon,
  Flex,
  IconButton,
  Kbd,
  SmileIcon,
} from '@buffer-mono/popcorn'

import type { FragmentOf } from '~publish/graphql'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import {
  EmojiPicker,
  useTextareaEmojiInsert,
} from '../../../../components/EmojiPicker'
import type { Service } from '../../graphqlEnums'
import { ReplyForm } from '../ReplyForm'
import { SavedRepliesPopover } from '../SavedRepliesPopover'
import type { SavedReplyItem_SavedReply } from '../SavedRepliesPopover/SavedReplyItem'

import { useTurnReplyIntoPostCoachmark } from './hooks/useTurnReplyIntoPostCoachmark'

const coachMarkContentStyle: React.CSSProperties &
  Record<`--${string}`, string> = {
  '--coach-mark-max-width': '350px',
}

const spotlightStyle: React.CSSProperties & Record<`--${string}`, string> = {
  '--spotlight-top': '50%',
  '--spotlight-left': '50%',
}

type TurnReplyIntoPostCoachmarkProps = {
  onDismiss: () => void
  onStartPost: () => void
  children: React.ReactNode
}

const TurnReplyIntoPostCoachmark = ({
  onDismiss,
  onStartPost,
  children,
}: TurnReplyIntoPostCoachmarkProps): JSX.Element => (
  <CoachMark defaultOpen={true} onDismiss={onDismiss} dismissTimeout={null}>
    <CoachMark.Overlay>
      <CoachMark.Spotlight size="medium" style={spotlightStyle} />
      {children}
    </CoachMark.Overlay>
    <CoachMark.Content style={coachMarkContentStyle} showCloseIcon={false}>
      <CoachMark.Title>Turn your reply into a post</CoachMark.Title>
      <CoachMark.Description>
        Use this reply to start a new post for your feed.
      </CoachMark.Description>
      <CoachMark.Footer>
        <Button variant="tertiary" onClick={onDismiss}>
          Maybe Later
        </Button>
        <Button variant="primary" onClick={onStartPost}>
          Start a Post
        </Button>
      </CoachMark.Footer>
    </CoachMark.Content>
  </CoachMark>
)

type StartPostButtonProps = {
  onClick: () => void
}

/** Media fields the "Start a Post" prefill reads from an engagement's assets. */
export type EngagementReplyFormAsset = {
  thumbnail: string
  source: string
}

export const StartPostButton = ({
  onClick,
}: StartPostButtonProps): JSX.Element => (
  <IconButton
    type="button"
    label="Start a Post"
    tooltip="Start a Post"
    variant="tertiary"
    size="medium"
    onClick={onClick}
  >
    <SquarePenIcon />
  </IconButton>
)

type EmojiPickerButtonProps = {
  emojiPickerRef: RefObject<HTMLDivElement>
  onEmojiSelect: (emoji: string) => void
}

export const EmojiPickerButton = ({
  emojiPickerRef,
  onEmojiSelect,
}: EmojiPickerButtonProps): JSX.Element => (
  <EmojiPicker onEmojiSelect={onEmojiSelect} emojiPickerRef={emojiPickerRef}>
    <IconButton label="Select emoji" variant="tertiary" tooltip="Select emoji">
      <SmileIcon />
    </IconButton>
  </EmojiPicker>
)

/** Tooltip for the reply submit button, naming its keyboard shortcut. */
export const SubmitReplyShortcutHint = (): JSX.Element => (
  <Flex gap="2xs" align="center">
    Submit reply
    <Flex gap="2xs" align="center">
      <Kbd color="inverted">mod</Kbd>
      <Kbd color="inverted">Enter</Kbd>
    </Flex>
  </Flex>
)

type UseReplyEmojiInsertOptions = {
  textareaRef: RefObject<HTMLTextAreaElement>
  replyText: string
  setReplyText: (text: string) => void
  maxChars: number
  channelService: Service
}

export const useReplyEmojiInsert = ({
  textareaRef,
  replyText,
  setReplyText,
  maxChars,
  channelService,
}: UseReplyEmojiInsertOptions): ((emoji: string) => void) => {
  // Wrap setReplyText with validation
  const setReplyTextWithValidation = useCallback(
    (text: string) => {
      // Only update if the new text doesn't exceed maxLength
      if (getCharsCountForService(text, channelService) <= maxChars) {
        setReplyText(text)
      }
    },
    [maxChars, channelService, setReplyText],
  )

  return useTextareaEmojiInsert(
    textareaRef,
    replyText,
    setReplyTextWithValidation,
  )
}

type EngagementReplyFormFooterProps = {
  maxChars: number
  disabled: boolean
  label?: string
  channelId: string
  channelService: Service
  textareaRef: RefObject<HTMLTextAreaElement>
  replyText: string
  setReplyText: (text: string) => void
  savedRepliesPopoverRef: RefObject<HTMLDivElement>
  handleInsertSavedReply: (
    savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>,
  ) => void
  emojiPickerRef: RefObject<HTMLDivElement>
  /** Assets attached to the engagement, used to prefill the "Start a Post" composer. */
  assets: EngagementReplyFormAsset[]
  /** Analytics CTA id for the "Start a Post" composer trigger. */
  cta: CTA
  /**
   * Feature-specific actions appended to the composer icon group. The footer
   * stays agnostic of what they do.
   */
  extraActions?: ReactNode
}

/**
 * Footer shared by comment and mention reply forms, composed from the
 * `ReplyForm` footer parts: emoji picker, saved-replies popover, a "Start a Post" composer trigger (with its long-reply
 * coachmark), the character counter, and the submit button. Prop-driven — the
 * per-feature reply forms supply the attached `assets` and analytics `cta`.
 */
export const EngagementReplyFormFooter = ({
  maxChars,
  disabled,
  label = 'Reply',
  channelId,
  channelService,
  textareaRef,
  replyText,
  setReplyText,
  savedRepliesPopoverRef,
  handleInsertSavedReply,
  emojiPickerRef,
  assets,
  cta,
  extraActions,
}: EngagementReplyFormFooterProps): JSX.Element => {
  const { createNewPostInComposer } = usePostComposer()

  // Coachmark for turning long replies into posts
  const { shouldShowCoachmark, onDismissCoachmark } =
    useTurnReplyIntoPostCoachmark(replyText.length)

  const handleEmojiSelect = useReplyEmojiInsert({
    textareaRef,
    replyText,
    setReplyText,
    maxChars,
    channelService,
  })

  // TODO: Review if we should use the actual width and height of the image
  // This is currently hardcoded because the types are mandatory and we don't have a way to get the actual width and height
  const prefillPostData = useMemo(() => {
    const media =
      assets.length > 0
        ? {
            thumbnail: assets[0].thumbnail,
            picture: assets[0].source,
            width: '',
            height: '',
            progress: '100',
            uploaded: 'true',
          }
        : undefined

    return { text: replyText, media }
  }, [replyText, assets])

  const handleStartPost = useCallback(() => {
    void createNewPostInComposer({
      cta,
      channels: [channelId],
      prefillPostData,
    })
  }, [createNewPostInComposer, cta, channelId, prefillPostData])

  const handleStartPostFromCoachmark = useCallback(() => {
    onDismissCoachmark()
    handleStartPost()
  }, [onDismissCoachmark, handleStartPost])

  const startPostTrigger = <StartPostButton onClick={handleStartPost} />

  return (
    <ReplyForm.Footer>
      <ReplyForm.FooterStart>
        <EmojiPickerButton
          emojiPickerRef={emojiPickerRef}
          onEmojiSelect={handleEmojiSelect}
        />

        <SavedRepliesPopover
          savedRepliesPopoverRef={savedRepliesPopoverRef}
          handleInsertSavedReply={handleInsertSavedReply}
        />

        {shouldShowCoachmark ? (
          <TurnReplyIntoPostCoachmark
            onDismiss={onDismissCoachmark}
            onStartPost={handleStartPostFromCoachmark}
          >
            {startPostTrigger}
          </TurnReplyIntoPostCoachmark>
        ) : (
          startPostTrigger
        )}
        {extraActions}
      </ReplyForm.FooterStart>

      <ReplyForm.FooterEnd>
        <ReplyForm.CharCount
          count={getCharsCountForService(replyText, channelService)}
          limit={maxChars}
        />
        <ReplyForm.Submit
          label={label}
          disabled={disabled}
          tooltip={<SubmitReplyShortcutHint />}
        />
      </ReplyForm.FooterEnd>
    </ReplyForm.Footer>
  )
}

EngagementReplyFormFooter.displayName = 'EngagementReplyFormFooter'
